// src/components/fulfillment/UploadSummaryModal.jsx
import React from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const eur = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
    : n;

function SummarySection({ title, data }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <List dense>
        {data.map((item, index) => (
          <ListItem key={index} disableGutters divider>
            <ListItemText primary={item.label} secondary={item.value} />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
}

export default function UploadSummaryModal({ open, onClose, data, onRebuildAllocation }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const s = data || {};

  const sections = [
    {
      title: 'Rows Imported',
      data: [
        { label: 'Warehouse (FUL)', value: s.warehouse_rows ?? 0 },
        { label: 'Transport (SHIP)', value: s.transport_rows ?? 0 },
      ],
    },
    {
      title: 'Article / Storage',
      data: [
        { label: 'Article rows', value: s.article_rows ?? 0 },
        { label: 'With SKU', value: s.article_with_sku ?? 0 },
        { label: 'Sum (net)', value: eur(s.article_sum_net ?? 0) },
      ],
    },
    {
      title: 'Anomalies',
      data: [
        { label: 'Missing ref number', value: s.missing_refnum_lines ?? 0 },
        { label: 'Zero/null amount', value: s.zero_or_null_amount_lines ?? 0 },
      ],
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle>
        Upload Summary
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Invoice: <strong>{s.invoice_number || '—'}</strong>
          {s.invoice_month && ` · Month: ${s.invoice_month}`}
        </Typography>
        {/*
          Der Allocation-Rebuild lief bis 2026-10-09 bei JEDEM Upload ins Leere
          (InvalidRequestError, vom Teardown still zurueckgerollt) — und der
          Wert stand in der Antwort, aber nirgends auf dem Schirm. Er gehoert
          hierher als ALARM, nicht als Listenzeile: ein stiller Fehlschlag, der
          wie eine Zahl aussieht, ist genau das, was ihn monatelang verdeckt hat.
        */}
        {s.allocation_rebuilt === false && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            /* 🔴 Der Handlungsknopf, nicht nur ein Ortshinweis. Die zwei anderen
               Fehler-Alarme derselben Seite tragen einen (`Try again`,
               FulfillmentKPIDashboard.jsx) — ein dritter ohne wäre ein
               Musterbruch. Und der Knopf liegt im ⋯-Menue, nicht sichtbar auf
               der Seite; „on the Fulfillment page" half dem Nutzer nicht. */
            action={
              onRebuildAllocation ? (
                <Button color="inherit" size="small" onClick={onRebuildAllocation}>
                  Rebuild allocation table
                </Button>
              ) : null
            }
          >
            <strong>The allocation table could not be rebuilt.</strong> The rows were
            imported, but the product allocation views are stale — they still show
            the state before this upload. Rebuild it here or in the “⋯” menu of the
            Fulfillment header, and check the server log for
            “Failed to rebuild allocation MV after upload”.
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {sections.map((sec) => (
            <Grid size={{ xs: 12, md: 4 }} key={sec.title}>
              <SummarySection title={sec.title} data={sec.data} />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
